import { FC, useEffect, useMemo, useState } from 'react'
import SVG from 'react-inlinesvg'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import {
  getPlatformBankAccounts,
  registerTransfer,
  RegisterTransferPayload,
} from '../../../services/payoutsService'
import { showErrorAlert } from '../../../helpers/alert'
import { handleApiError } from '../../../utils/handleApiError'

interface PlatformBank {
  id: number
  name: string
  bankName: string
  currency: string
  country: string
  isActive: boolean
}

interface Props {
  isOpen: boolean
  payoutId: number
  outstandingBalance: string
  currency: string
  onClose: () => void
  onSaved: () => void
}

const genIdempotencyKey = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? (crypto as any).randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`)

const RegisterTransferModal: FC<Props> = ({ isOpen, payoutId, outstandingBalance, currency, onClose, onSaved }) => {
  const [banks, setBanks] = useState<PlatformBank[]>([])
  const [loading, setLoading] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState<string>('')

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])

  const [form, setForm] = useState({
    transferDate: today,
    amount: outstandingBalance,
    originatingBank: '',
    reference: '',
    notes: '',
    markedAs: 'in_transit' as 'in_transit' | 'paid',
    beneficiaryAccountHolder: '',
    beneficiaryBankName: '',
    beneficiaryAccountNumber: '',
    beneficiaryCountry: '',
  })

  useEffect(() => {
    if (!isOpen) return
    setIdempotencyKey(genIdempotencyKey())
    setForm((f) => ({ ...f, amount: outstandingBalance, transferDate: today }))
    getPlatformBankAccounts()
      .then((data) => setBanks(Array.isArray(data) ? data : data.results ?? []))
      .catch(() => setBanks([]))
  }, [isOpen, outstandingBalance, today])

  if (!isOpen) return null

  const setField = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const validate = () => {
    if (!form.transferDate) return 'Transfer date is required.'
    if (new Date(form.transferDate) > new Date()) return 'Transfer date cannot be in the future.'
    if (!form.amount || Number(form.amount) <= 0) return 'Amount must be greater than zero.'
    if (Number(form.amount) > Number(outstandingBalance) + 0.01)
      return `Amount exceeds outstanding balance (${outstandingBalance} ${currency}).`
    if (!form.originatingBank) return 'Originating bank is required.'
    if (!form.reference.trim()) return 'Reference / folio is required.'
    return null
  }

  const handleSave = async () => {
    const error = validate()
    if (error) {
      showErrorAlert({ title: 'Validation', message: error })
      return
    }
    setLoading(true)
    try {
      const payload: RegisterTransferPayload = {
        transferDate: form.transferDate,
        amount: form.amount,
        originatingBank: Number(form.originatingBank),
        reference: form.reference.trim(),
        notes: form.notes.trim() || undefined,
        markedAs: form.markedAs,
        idempotencyKey,
        beneficiaryAccountHolder: form.beneficiaryAccountHolder.trim() || undefined,
        beneficiaryBankName: form.beneficiaryBankName.trim() || undefined,
        beneficiaryAccountNumber: form.beneficiaryAccountNumber.trim() || undefined,
        beneficiaryCountry: form.beneficiaryCountry.trim().toUpperCase().slice(0, 2) || undefined,
      }
      await registerTransfer(payoutId, payload)
      onSaved()
    } catch (err: any) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({ title: 'Error', message: 'Could not register the transfer.' }),
      })
    } finally {
      setLoading(false)
    }
  }

  const isPartial = Number(form.amount) > 0 && Number(form.amount) < Number(outstandingBalance) - 0.01

  const formatBalance = () => {
    const value = Number(outstandingBalance)
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(value)
    } catch {
      return `${value.toFixed(2)} ${currency}`
    }
  }

  return (
    <div className='nb-modal-overlay nb-modal-overlay--over-drawer' onClick={onClose}>
      <div className='nb-transfer-modal' onClick={(e) => e.stopPropagation()}>
        <div className='nb-transfer-modal__header'>
          <div className='nb-transfer-modal__title-group'>
            <span className='nb-transfer-modal__eyebrow'>Payout #{payoutId}</span>
            <h2 className='nb-transfer-modal__title'>Register Transfer</h2>
          </div>
          <button type='button' className='nb-transfer-modal__close' onClick={onClose} aria-label='Close'>
            <SVG src={toAbsoluteUrl('/media/svg/nobilis/closeX.svg')} className='rm-style-4' />
          </button>
        </div>

        <div className='nb-transfer-modal__balance-strip'>
          <span className='nb-transfer-modal__balance-label'>Outstanding balance</span>
          <span className='nb-transfer-modal__balance-value'>{formatBalance()}</span>
        </div>

        <div className='nb-transfer-modal__body'>
          <div className='nb-transfer-modal__section'>
            <div className='nb-transfer-modal__grid'>
              <div className='nb-transfer-modal__field'>
                <label className='nb-transfer-modal__label'>Transfer date</label>
                <input
                  type='date'
                  className='nb-transfer-modal__input'
                  max={today}
                  value={form.transferDate}
                  onChange={(e) => setField('transferDate', e.target.value)}
                />
              </div>

              <div className='nb-transfer-modal__field'>
                <label className='nb-transfer-modal__label'>Amount ({currency})</label>
                <input
                  type='number'
                  className='nb-transfer-modal__input'
                  step='0.01'
                  min='0'
                  value={form.amount}
                  onChange={(e) => setField('amount', e.target.value)}
                />
                {isPartial && (
                  <span className='nb-transfer-modal__hint nb-transfer-modal__hint--warning'>
                    Partial transfer — payout stays In Transit until fully settled.
                  </span>
                )}
              </div>

              <div className='nb-transfer-modal__field nb-transfer-modal__field--full'>
                <label className='nb-transfer-modal__label'>Originating bank</label>
                <select
                  className='nb-transfer-modal__select'
                  value={form.originatingBank}
                  onChange={(e) => setField('originatingBank', e.target.value)}
                >
                  <option value=''>Select a bank…</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} · {b.currency}
                    </option>
                  ))}
                </select>
              </div>

              <div className='nb-transfer-modal__field nb-transfer-modal__field--full'>
                <label className='nb-transfer-modal__label'>Reference / folio number</label>
                <input
                  type='text'
                  className='nb-transfer-modal__input'
                  maxLength={64}
                  placeholder='SPEI ref, wire number, Wise id…'
                  value={form.reference}
                  onChange={(e) => setField('reference', e.target.value)}
                />
              </div>

              <div className='nb-transfer-modal__field nb-transfer-modal__field--full'>
                <label className='nb-transfer-modal__label'>Notes (optional)</label>
                <textarea
                  className='nb-transfer-modal__textarea'
                  maxLength={500}
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className='nb-transfer-modal__section'>
            <span className='nb-transfer-modal__section-title'>Beneficiary bank details</span>
            <div className='nb-transfer-modal__beneficiary-card'>
              <div className='nb-transfer-modal__grid'>
                <div className='nb-transfer-modal__field nb-transfer-modal__field--full'>
                  <label className='nb-transfer-modal__label'>Account holder</label>
                  <input
                    type='text'
                    className='nb-transfer-modal__input'
                    placeholder='Full legal name'
                    value={form.beneficiaryAccountHolder}
                    onChange={(e) => setField('beneficiaryAccountHolder', e.target.value)}
                  />
                </div>
                <div className='nb-transfer-modal__field'>
                  <label className='nb-transfer-modal__label'>Bank name</label>
                  <input
                    type='text'
                    className='nb-transfer-modal__input'
                    placeholder='e.g. BBVA'
                    value={form.beneficiaryBankName}
                    onChange={(e) => setField('beneficiaryBankName', e.target.value)}
                  />
                </div>
                <div className='nb-transfer-modal__field'>
                  <label className='nb-transfer-modal__label'>Country (ISO)</label>
                  <input
                    type='text'
                    className='nb-transfer-modal__input'
                    placeholder='MX'
                    maxLength={2}
                    value={form.beneficiaryCountry}
                    onChange={(e) => setField('beneficiaryCountry', e.target.value.toUpperCase())}
                  />
                </div>
                <div className='nb-transfer-modal__field nb-transfer-modal__field--full'>
                  <label className='nb-transfer-modal__label'>Account number / CLABE / IBAN</label>
                  <input
                    type='text'
                    className='nb-transfer-modal__input'
                    placeholder='Beneficiary account number'
                    value={form.beneficiaryAccountNumber}
                    onChange={(e) => setField('beneficiaryAccountNumber', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='nb-transfer-modal__section'>
            <span className='nb-transfer-modal__section-title'>Mark payout as</span>
            <div className='nb-transfer-modal__segmented' role='radiogroup' aria-label='Mark payout as'>
              <button
                type='button'
                role='radio'
                aria-checked={form.markedAs === 'in_transit'}
                className={`nb-transfer-modal__segmented-option${form.markedAs === 'in_transit' ? ' nb-transfer-modal__segmented-option--active' : ''}`}
                onClick={() => setField('markedAs', 'in_transit')}
              >
                <span className='nb-transfer-modal__segmented-option-dot' />
                In Transit
              </button>
              <button
                type='button'
                role='radio'
                aria-checked={form.markedAs === 'paid'}
                className={`nb-transfer-modal__segmented-option${form.markedAs === 'paid' ? ' nb-transfer-modal__segmented-option--active' : ''}`}
                onClick={() => setField('markedAs', 'paid')}
              >
                <span className='nb-transfer-modal__segmented-option-dot' />
                Paid
              </button>
            </div>
          </div>
        </div>

        <div className='nb-transfer-modal__footer'>
          <button type='button' className='nb-transfer-modal__btn nb-transfer-modal__btn--ghost' onClick={onClose}>
            Cancel
          </button>
          <button
            type='button'
            className='nb-transfer-modal__btn nb-transfer-modal__btn--primary'
            disabled={loading}
            onClick={handleSave}
          >
            {loading ? 'Saving…' : 'Save Transfer'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RegisterTransferModal
